import {
  AdditiveBlending,
  BoxGeometry,
  CanvasTexture,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Quaternion,
  RingGeometry,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace,
  Vector3,
} from 'three';
import { clamp } from '../../../utils/math.js';
import { getSmokeTexture } from '../../../utils/textures.js';

const DEFAULT_CONFIG = {
  firstDelay: 15,
  intervalMin: 28,
  intervalMax: 42,
  duration: 5,
  rocketTimes: [1.1, 2.8],
  rocketFlightTime: 1.55,
  damage: 50,
  blastRadius: 5.5,
  aimSpread: 2.2,
  hoverForward: 28,
  hoverSide: 6,
  hoverHeight: 6.0,
  trafficPanicSpeed: 1.55,
};

const FORWARD = new Vector3(0, 0, 1);
const EXPLOSION_DURATION = 1.1;
const EXPLOSION_POOL_SIZE = 3;

const RING_GEOMETRY = new RingGeometry(0.9, 1, 24);
RING_GEOMETRY.rotateX(-Math.PI / 2);

const ROCKET_BODY_GEOMETRY = new CylinderGeometry(0.1, 0.13, 0.95, 8);
ROCKET_BODY_GEOMETRY.rotateX(Math.PI / 2);
const ROCKET_NOSE_GEOMETRY = new ConeGeometry(0.12, 0.36, 8);
ROCKET_NOSE_GEOMETRY.rotateX(Math.PI / 2);
const ROCKET_FIN_GEOMETRY = new BoxGeometry(0.55, 0.035, 0.26);
const ROCKET_EXHAUST_GEOMETRY = new ConeGeometry(0.15, 1.05, 8);
ROCKET_EXHAUST_GEOMETRY.rotateX(-Math.PI / 2);
const ROCKET_GLOW_GEOMETRY = new SphereGeometry(0.2, 8, 6);
const ROCKET_TRAIL_GEOMETRY = new CylinderGeometry(0.05, 0.24, 4.2, 6);
ROCKET_TRAIL_GEOMETRY.rotateX(Math.PI / 2);

const HOVER_POINT = new Vector3();
const ROCKET_START = new Vector3();
const ROCKET_TARGET = new Vector3();
const ROCKET_DIRECTION = new Vector3();
const ROCKET_QUATERNION = new Quaternion();

/**
 * A periodic RSF quadcopter raid.
 *
 * The drone hovers ahead of and above the truck so it stays in the chase
 * camera's view. Each five-second pass launches two rockets from its pods.
 * A rocket aims at the truck's position at launch; the impact deals 50 health
 * damage if the truck is still inside the blast radius by the time it lands.
 */
export class DroneAttack {
  constructor(level, config = {}) {
    this.level = level;
    this.config = { ...DEFAULT_CONFIG, ...config };

    this.onAttackStart = null;
    this.onAttackEnd = null;
    this.onRocketLaunch = null;
    this.onImpact = null;

    this.active = false;
    this.elapsed = 0;
    this.cooldown = this.config.firstDelay;
    this.time = 0;
    this.rocketIndex = 0;
    this.entry = 0;
    this.attackId = 0;

    this.rockets = [];
    this._forward = new Vector3();
    this._right = new Vector3();
    this._entryStart = new Vector3();
    this._hover = new Vector3();

    this._rocketMaterials = {
      body: new MeshStandardMaterial({
        color: 0x3e4438,
        roughness: 0.54,
        metalness: 0.42,
      }),
      tip: new MeshStandardMaterial({
        color: 0x8d2f27,
        roughness: 0.48,
        metalness: 0.28,
      }),
      exhaust: new MeshBasicMaterial({
        color: 0xffb35b,
        transparent: true,
        opacity: 0.94,
        blending: AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
      smoke: new MeshBasicMaterial({
        color: 0xa39a8c,
        transparent: true,
        opacity: 0.34,
        depthWrite: false,
      }),
    };

    const visual = createDroneVisual();
    this.drone = visual.group;
    this.rotors = visual.rotors;
    this.beaconMaterial = visual.beaconMaterial;
    this.level.root.add(this.drone);

    this.explosions = [];
    for (let i = 0; i < EXPLOSION_POOL_SIZE; i += 1) {
      this.explosions.push(this.#createExplosion());
    }
  }

  update(dt, player) {
    const step = clamp(dt, 0, 0.1);
    this.time += step;

    // Rockets and their fireballs keep animating after the drone has left.
    this.#updateExplosions(step);
    this.#updateRockets(step, player);

    if (!player?.mesh) return;

    if (!this.active) {
      if ((player.health ?? 0) <= 0) return;

      this.cooldown -= step;
      if (this.cooldown <= 0) this.#startAttack(player);
      return;
    }

    this.elapsed += step;
    this.#placeDrone(step, player);
    this.#animateDrone(step);
    this.#launchDueRockets(player);

    if (this.elapsed >= this.config.duration) this.#endAttack();
  }

  /** Cancel any live raid and restore the normal attack countdown. */
  reset() {
    if (this.active) {
      this.active = false;
      this.onAttackEnd?.({ reason: 'reset' });
    }

    this.drone.visible = false;
    this.elapsed = 0;
    this.entry = 0;
    this.rocketIndex = 0;
    this.cooldown = this.config.firstDelay;

    for (const rocket of this.rockets) rocket.group.removeFromParent();
    this.rockets.length = 0;

    for (const effect of this.explosions) {
      effect.active = false;
      effect.time = 0;
      effect.group.visible = false;
    }
  }

  #startAttack(player) {
    this.active = true;
    this.elapsed = 0;
    this.entry = 0;
    this.rocketIndex = 0;
    this.attackId += 1;
    this.cooldown = 0;

    this.#hoverPoint(player, this._hover);
    this._entryStart
      .copy(this._hover)
      .addScaledVector(this._forward, 24);
    this._entryStart.y += 16;

    this.drone.position.copy(this._entryStart);
    this.#facePlayer(player);
    this.drone.visible = true;
    this.drone.updateMatrixWorld(true);

    this.onAttackStart?.({
      attackId: this.attackId,
      duration: this.config.duration,
      rocketCount: this.config.rocketTimes.length,
    });
  }

  #endAttack() {
    this.active = false;
    this.elapsed = 0;
    this.drone.visible = false;
    this.cooldown = this.config.intervalMin
      + Math.random() * (this.config.intervalMax - this.config.intervalMin);
    this.onAttackEnd?.({ attackId: this.attackId });
  }

  #placeDrone(dt, player) {
    this.#hoverPoint(player, HOVER_POINT);

    if (this.entry < 1) {
      this.entry = Math.min(1, this.entry + dt / 0.85);
      const eased = this.entry * this.entry * (3 - 2 * this.entry);
      this.drone.position.lerpVectors(this._entryStart, HOVER_POINT, eased);
    } else {
      const blend = 1 - Math.exp(-6.5 * dt);
      this.drone.position.lerp(HOVER_POINT, blend);
    }

    this.#facePlayer(player);
    this.drone.rotation.x = -0.07 + Math.sin(this.elapsed * 2.2) * 0.025;
    this.drone.rotation.z = Math.sin(this.elapsed * 1.7) * 0.085;
    this.drone.updateMatrixWorld(true);
  }

  #facePlayer(player) {
    const dx = player.mesh.position.x - this.drone.position.x;
    const dz = player.mesh.position.z - this.drone.position.z;
    this.drone.rotation.y = Math.atan2(dx, dz);
  }

  #hoverPoint(player, out) {
    this.#playerAxes(player);

    const sway = Math.sin(this.elapsed * 1.15 + 0.6) * 2.6;
    out.copy(player.mesh.position)
      .addScaledVector(this._forward, this.config.hoverForward)
      .addScaledVector(this._right, this.config.hoverSide + sway);
    out.y = player.mesh.position.y
      + this.config.hoverHeight
      + Math.sin(this.elapsed * 2.3) * 0.55;
    return out;
  }

  #playerAxes(player) {
    this._forward.set(0, 0, 1).applyQuaternion(player.mesh.quaternion);
    this._forward.y = 0;
    if (this._forward.lengthSq() < 1e-5) this._forward.set(0, 0, 1);
    else this._forward.normalize();

    this._right.set(1, 0, 0).applyQuaternion(player.mesh.quaternion);
    this._right.y = 0;
    if (this._right.lengthSq() < 1e-5) this._right.set(1, 0, 0);
    else this._right.normalize();
  }

  #animateDrone(dt) {
    for (let i = 0; i < this.rotors.length; i += 1) {
      this.rotors[i].rotation.y += dt * (46 + (i % 2) * 7);
    }
    this.beaconMaterial.color.setHex(
      Math.sin(this.time * 22) > 0 ? 0xff3a2c : 0x52120f,
    );
  }

  #launchDueRockets(player) {
    const times = this.config.rocketTimes;
    while (
      this.rocketIndex < times.length
      && this.elapsed >= times[this.rocketIndex]
    ) {
      this.#launchRocket(player, this.rocketIndex);
      this.rocketIndex += 1;
    }
  }

  #launchRocket(player, index) {
    this.drone.updateMatrixWorld(true);

    const side = index % 2 === 0 ? 0.44 : -0.44;
    ROCKET_START.set(side, -0.16, 1.08);
    this.drone.localToWorld(ROCKET_START);

    ROCKET_TARGET.copy(player.mesh.position);
    ROCKET_TARGET.x += (Math.random() * 2 - 1) * this.config.aimSpread;
    ROCKET_TARGET.z += (Math.random() * 2 - 1) * this.config.aimSpread;
    ROCKET_TARGET.y = Math.max(0.2, player.mesh.position.y - 0.85);

    const rocket = this.#createRocket();
    rocket.group.position.copy(ROCKET_START);
    ROCKET_DIRECTION.copy(ROCKET_TARGET).sub(ROCKET_START).normalize();
    ROCKET_QUATERNION.setFromUnitVectors(FORWARD, ROCKET_DIRECTION);
    rocket.group.quaternion.copy(ROCKET_QUATERNION);
    this.level.root.add(rocket.group);

    this.rockets.push({
      group: rocket.group,
      start: ROCKET_START.clone(),
      target: ROCKET_TARGET.clone(),
      elapsed: 0,
      index,
    });

    this.onRocketLaunch?.({
      attackId: this.attackId,
      index,
      position: ROCKET_START.clone(),
    });
  }

  #updateRockets(dt, player) {
    for (let i = this.rockets.length - 1; i >= 0; i -= 1) {
      const rocket = this.rockets[i];
      rocket.elapsed += dt;
      const t = clamp(
        rocket.elapsed / Math.max(0.2, this.config.rocketFlightTime),
        0,
        1,
      );

      rocket.group.position.lerpVectors(rocket.start, rocket.target, t);
      rocket.group.position.y += Math.sin(Math.PI * t) * 1.1;

      if (t < 1) continue;

      rocket.group.position.copy(rocket.target);
      this.#spawnExplosion(rocket.target);

      let distance = Infinity;
      const playerPosition = player?.mesh?.position;
      if (playerPosition) {
        distance = playerPosition.distanceTo(rocket.target);
      }
      const hit = distance <= this.config.blastRadius;
      const damage = hit
        ? (player?.damage?.(this.config.damage, {
          source: 'drone-rocket',
          attackId: this.attackId,
          rocketIndex: rocket.index,
          distance,
        }) ?? 0)
        : 0;

      this.onImpact?.({
        attackId: this.attackId,
        position: rocket.target.clone(),
        hit,
        distance,
        damage,
      });

      rocket.group.removeFromParent();
      this.rockets.splice(i, 1);
    }
  }

  #createRocket() {
    const group = new Group();
    group.name = 'rsf-drone-rocket';

    const body = new Mesh(ROCKET_BODY_GEOMETRY, this._rocketMaterials.body);
    const nose = new Mesh(ROCKET_NOSE_GEOMETRY, this._rocketMaterials.tip);
    nose.position.z = 0.64;
    const finH = new Mesh(ROCKET_FIN_GEOMETRY, this._rocketMaterials.body);
    finH.position.z = -0.34;
    const finV = new Mesh(ROCKET_FIN_GEOMETRY, this._rocketMaterials.body);
    finV.position.z = -0.34;
    finV.rotation.z = Math.PI / 2;
    const exhaust = new Mesh(
      ROCKET_EXHAUST_GEOMETRY,
      this._rocketMaterials.exhaust,
    );
    exhaust.position.z = -0.72;
    const glow = new Mesh(
      ROCKET_GLOW_GEOMETRY,
      this._rocketMaterials.exhaust,
    );
    glow.position.z = -0.64;
    glow.scale.setScalar(1.4);
    const trail = new Mesh(
      ROCKET_TRAIL_GEOMETRY,
      this._rocketMaterials.smoke,
    );
    trail.position.z = -2.55;

    group.add(body, nose, finH, finV, exhaust, glow, trail);
    group.scale.setScalar(1.15);
    return { group };
  }

  #createExplosion() {
    const group = new Group();
    group.name = 'rsf-drone-explosion';
    group.visible = false;

    const fireMaterial = new SpriteMaterial({
      map: getFireTexture(),
      color: 0xff9838,
      transparent: true,
      opacity: 0,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    });
    const coreMaterial = new SpriteMaterial({
      map: getFireTexture(),
      color: 0xfff1b5,
      transparent: true,
      opacity: 0,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    });
    const smokeMaterial = new SpriteMaterial({
      map: getSmokeTexture(),
      color: 0x756957,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const ringMaterial = new MeshBasicMaterial({
      color: 0xffc166,
      transparent: true,
      opacity: 0,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    });

    const fire = new Sprite(fireMaterial);
    const core = new Sprite(coreMaterial);
    const ring = new Mesh(RING_GEOMETRY, ringMaterial);
    ring.position.y = 0.08;

    const smoke = new Group();
    for (const [x, y, z, scale] of [
      [0, 0.4, 0, 2.1],
      [-0.9, 0.1, 0.5, 1.35],
      [0.8, 0.65, -0.4, 1.6],
    ]) {
      const puff = new Sprite(smokeMaterial);
      puff.position.set(x, y, z);
      puff.scale.setScalar(scale);
      smoke.add(puff);
    }

    group.add(fire, core, smoke, ring);
    this.level.root.add(group);

    return {
      active: false,
      time: 0,
      group,
      fire,
      core,
      smoke,
      ring,
      fireMaterial,
      coreMaterial,
      smokeMaterial,
      ringMaterial,
    };
  }

  #spawnExplosion(position) {
    let effect = this.explosions.find((candidate) => !candidate.active);
    if (!effect) {
      effect = this.explosions.reduce((oldest, candidate) => (
        candidate.time > oldest.time ? candidate : oldest
      ));
    }

    effect.active = true;
    effect.time = 0;
    effect.group.visible = true;
    effect.group.position.copy(position);

    effect.fire.scale.setScalar(1.2);
    effect.fire.position.y = 0.55;
    effect.fireMaterial.opacity = 0.95;
    effect.core.scale.setScalar(0.6);
    effect.core.position.y = 0.5;
    effect.coreMaterial.opacity = 1;
    effect.smoke.scale.setScalar(0.55);
    effect.smoke.position.y = 0.9;
    effect.smokeMaterial.opacity = 0.34;
    effect.ring.scale.setScalar(0.5);
    effect.ringMaterial.opacity = 0.65;
  }

  #updateExplosions(dt) {
    for (const effect of this.explosions) {
      if (!effect.active) continue;

      effect.time += dt;
      const t = effect.time / EXPLOSION_DURATION;
      if (t >= 1) {
        effect.active = false;
        effect.time = 0;
        effect.group.visible = false;
        continue;
      }

      const expansion = 1 - Math.pow(1 - Math.min(1, t / 0.62), 3);
      const fade = Math.max(0, 1 - t);

      effect.fire.scale.setScalar(1.2 + expansion * 3.2);
      effect.fire.position.y = 0.55 + expansion * 1.25;
      effect.fireMaterial.opacity = Math.max(0, 0.95 - t * 2.15);

      effect.core.scale.setScalar(0.6 + expansion * 1.4);
      effect.core.position.y = 0.5 + expansion * 0.8;
      effect.coreMaterial.opacity = Math.max(0, 1 - t * 3.8);

      effect.smoke.scale.setScalar(0.55 + expansion * 1.05);
      effect.smoke.position.y = 0.9 + expansion * 4.2;
      effect.smokeMaterial.opacity = 0.34 * Math.pow(fade, 1.4);

      effect.ring.scale.setScalar(0.5 + expansion * 2.8);
      effect.ringMaterial.opacity = 0.65 * fade * fade;
    }
  }
}

export function buildDroneAttack(level) {
  return new DroneAttack(level, level.config?.droneAttack);
}

let fireTexture = null;

function getFireTexture() {
  if (fireTexture) return fireTexture;

  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  const gradient = context.createRadialGradient(
    size * 0.5,
    size * 0.5,
    0,
    size * 0.5,
    size * 0.5,
    size * 0.5,
  );
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(0.22, 'rgba(255, 244, 190, 0.95)');
  gradient.addColorStop(0.52, 'rgba(255, 166, 44, 0.72)');
  gradient.addColorStop(0.78, 'rgba(226, 88, 18, 0.28)');
  gradient.addColorStop(1, 'rgba(140, 40, 0, 0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);

  fireTexture = new CanvasTexture(canvas);
  fireTexture.colorSpace = SRGBColorSpace;
  return fireTexture;
}

function createDroneVisual() {
  const group = new Group();
  group.name = 'rsf-drone';
  group.visible = false;

  const bodyMaterial = new MeshStandardMaterial({
    color: 0x5b6049,
    roughness: 0.66,
    metalness: 0.26,
    emissive: 0x1d2018,
    emissiveIntensity: 0.65,
  });
  const darkMaterial = new MeshStandardMaterial({
    color: 0x2a2e29,
    roughness: 0.54,
    metalness: 0.36,
    emissive: 0x151715,
    emissiveIntensity: 0.55,
  });
  const accentMaterial = new MeshStandardMaterial({
    color: 0x9d3329,
    roughness: 0.52,
    metalness: 0.24,
    emissive: 0x3a0d09,
    emissiveIntensity: 0.5,
  });
  const glassMaterial = new MeshStandardMaterial({
    color: 0x172329,
    roughness: 0.16,
    metalness: 0.62,
  });
  const rotorMaterial = new MeshStandardMaterial({
    color: 0x121514,
    roughness: 0.82,
    metalness: 0.06,
  });
  const beaconMaterial = new MeshBasicMaterial({
    color: 0xff3a2c,
    toneMapped: false,
  });

  const body = new Mesh(new BoxGeometry(1.25, 0.28, 1.9), bodyMaterial);
  body.castShadow = true;
  const deck = new Mesh(new BoxGeometry(0.9, 0.12, 1.16), darkMaterial);
  deck.position.y = 0.2;
  deck.castShadow = true;
  const nose = new Mesh(new BoxGeometry(0.58, 0.22, 0.42), darkMaterial);
  nose.position.set(0, 0.01, 0.98);
  nose.castShadow = true;

  const band = new Mesh(new BoxGeometry(1.28, 0.05, 0.24), accentMaterial);
  band.position.set(0, 0.15, -0.18);
  const bellyBand = new Mesh(new BoxGeometry(0.7, 0.035, 0.3), accentMaterial);
  bellyBand.position.set(0, -0.16, -0.08);
  const spine = new Mesh(new BoxGeometry(0.08, 0.1, 1.3), darkMaterial);
  spine.position.set(0, 0.27, -0.05);

  const camera = new Mesh(new SphereGeometry(0.21, 8, 6), glassMaterial);
  camera.scale.set(1, 0.7, 1);
  camera.position.set(0, -0.2, 0.54);
  const lens = new Mesh(new SphereGeometry(0.075, 7, 5), accentMaterial);
  lens.position.set(0, -0.22, 0.72);

  group.add(body, deck, nose, band, bellyBand, spine, camera, lens);

  const armGeometry = new BoxGeometry(2.8, 0.075, 0.14);
  for (const angle of [Math.PI / 4, -Math.PI / 4]) {
    const arm = new Mesh(armGeometry, darkMaterial);
    arm.position.y = 0.08;
    arm.rotation.y = angle;
    arm.castShadow = true;
    group.add(arm);
  }

  const rotorGeometry = new BoxGeometry(1.1, 0.025, 0.105);
  const hubGeometry = new CylinderGeometry(0.18, 0.2, 0.13, 8);
  const rotors = [];
  for (const x of [-0.99, 0.99]) {
    for (const z of [-0.99, 0.99]) {
      const hub = new Mesh(hubGeometry, darkMaterial);
      hub.position.set(x, 0.15, z);
      hub.castShadow = true;
      group.add(hub);

      const rotor = new Group();
      rotor.position.set(x, 0.23, z);
      const bladeA = new Mesh(rotorGeometry, rotorMaterial);
      const bladeB = new Mesh(rotorGeometry, rotorMaterial);
      bladeB.rotation.y = Math.PI / 2;
      rotor.add(bladeA, bladeB);
      group.add(rotor);
      rotors.push(rotor);
    }
  }

  const podGeometry = new CylinderGeometry(0.13, 0.16, 0.95, 8);
  podGeometry.rotateX(Math.PI / 2);
  for (const x of [-0.44, 0.44]) {
    const pod = new Mesh(podGeometry, bodyMaterial);
    pod.position.set(x, -0.14, 0.5);
    pod.castShadow = true;
    group.add(pod);

    const podTip = new Mesh(new BoxGeometry(0.24, 0.08, 0.08), accentMaterial);
    podTip.position.set(x, -0.14, 1.0);
    group.add(podTip);
  }

  const antenna = new Mesh(new CylinderGeometry(0.012, 0.018, 0.38, 5), darkMaterial);
  antenna.position.set(0, 0.46, -0.58);
  const beacon = new Mesh(new SphereGeometry(0.065, 7, 5), beaconMaterial);
  beacon.position.set(0, 0.61, -0.58);
  group.add(antenna, beacon);

  group.scale.setScalar(1.4);
  return { group, rotors, beaconMaterial };
}
